import React from 'react'
import PropTypes from 'prop-types'
import * as Types from 'types'
import * as Sentry from '@sentry/browser'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { selectors } from '../reducer'
import * as apiActions from 'api-actions'
import { flashErrorMessage } from 'redux-flash'
import { Path } from 'config/portal'
import { NO_SERVICES_AGREEMENT_OPTION } from 'config/automatic-rollovers'
import { TOAST_ERROR_CONTACT_SUPPORT } from 'config/notifications'
import {
  formatCurrency,
  formatFileName,
  useParticipantsSummaryTaxableAccounts,
  downloadDiscardedParticipants,
} from 'utils'
import { useHistory } from 'react-router-dom'
import { FlowActions, ParticipantsSummaryTable } from '../components'
import { some, map } from 'lodash'
import { selectors as apiSelectors } from 'lp-redux-api'
import { RecordTypes } from 'flatfile-config'
import SharedReviewAndSubmitView from '../../products/views/ReviewAndSubmitView'

const FILE_NAME_PREFIX = 'RSP_'
const FILE_NAME_MANUAL_ENTRY = 'RSP_Inspira_Financial_Manual_Entry.xlsx'

const DISCARDED_PARTICIPANTS_TEXT =
  'Some participants will not be included in your submission due to issues with those participant\u2019s data, their plan type and status, agreement status, or their taxable amount. Please download the data for these participants for your records and resubmit them after the issues have been resolved.'

const scrubDollarSign = (val) => val.replaceAll(/[$]/g, '')

const propTypes = {
  participantsAgreementHash: PropTypes.object,
  participantFileName: PropTypes.string,
  createTaxableAccountsParticipantsData: PropTypes.func.isRequired,
  flashErrorMessageHandler: PropTypes.func.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  discardedParticipants: PropTypes.arrayOf(Types.rolloverParticipant)
    .isRequired,
  isSubmittingParticipantsData: PropTypes.bool.isRequired,
}

const defaultProps = {
  participantsAgreementHash: null,
  participantFileName: null,
}

function ReviewAndSubmitView({
  participantsAgreementHash,
  participantFileName,
  createTaxableAccountsParticipantsData,
  flashErrorMessageHandler,
  participantsForSubmission,
  discardedParticipants,
  isSubmittingParticipantsData,
}) {
  const history = useHistory()
  const { data: summaryData, overview: summaryOverview } =
    useParticipantsSummaryTaxableAccounts(participantsForSubmission)

  const handleSubmit = async () => {
    const isPartialSubmission = some(
      participantsAgreementHash,
      ({ servicesAgreement }) =>
        servicesAgreement === NO_SERVICES_AGREEMENT_OPTION.value
    )

    const scrubbedParticipantsForSubmission = map(
      participantsForSubmission,
      ({ taxableAmount, ...rest }) => ({
        ...rest,
        taxableAmount: scrubDollarSign(taxableAmount),
      })
    )

    try {
      await createTaxableAccountsParticipantsData({
        fileName: participantFileName
          ? formatFileName(participantFileName, FILE_NAME_PREFIX)
          : FILE_NAME_MANUAL_ENTRY,
        participants: scrubbedParticipantsForSubmission,
        summary: {
          totalPlans: summaryOverview.totalPlans,
          totalParticipants: summaryOverview.totalParticipants,
          totalTaxableAmount: formatCurrency(summaryOverview.taxableAmount),
        },
      })
      history.push({
        pathname: Path.TAXABLE_ACCOUNTS_SERVICES_AGREEMENTS,
        state: {
          ignorePrompt: true,
          showTaxableAccountsSuccessModal: true,
          isPartialSubmission,
        },
      })
    } catch (e) {
      Sentry.captureException(e)
      flashErrorMessageHandler(TOAST_ERROR_CONTACT_SUPPORT)
    }
  }

  const handleDownload = () => {
    downloadDiscardedParticipants({
      participants: discardedParticipants,
      originalFileName: participantFileName,
      recordType: RecordTypes.TAXABLE_ACCOUNTS_PARTICIPANT,
    })
  }

  return (
    <SharedReviewAndSubmitView
      participantsAgreementHash={participantsAgreementHash}
      participantsForSubmission={participantsForSubmission}
      discardedParticipants={discardedParticipants}
      isSubmittingParticipantsData={isSubmittingParticipantsData}
      onSubmit={handleSubmit}
      onDownload={handleDownload}
      uploadFilePath="/taxable-accounts/upload-file"
      summaryData={summaryData}
      SummaryTableComponent={ParticipantsSummaryTable}
      FlowActionsComponent={FlowActions}
      discardedParticipantsText={DISCARDED_PARTICIPANTS_TEXT}
    />
  )
}

ReviewAndSubmitView.propTypes = propTypes
ReviewAndSubmitView.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    participantFileName: selectors.participantFileName(state),
    participantsAgreementHash: selectors.participantsAgreementHash(state),
    participantsForSubmission: selectors.participantsForSubmission(state),
    discardedParticipants: selectors.discardedParticipants(state),
    isSubmittingParticipantsData: apiSelectors.isLoading(
      state,
      apiActions.createTaxableAccountsParticipantsData
    ),
  }
}

const mapDispatchToProps = {
  createTaxableAccountsParticipantsData:
    apiActions.createTaxableAccountsParticipantsData,
  flashErrorMessageHandler: flashErrorMessage,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ReviewAndSubmitView
)
